'use client';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useSession } from '@/lib/session';
import { loadStripeWithCapture } from '@/lib/stripe-loader';
import { cn } from '@/lib/utils';
import type {
  ClinicPage,
  ClinicStep,
} from '@borradh-workspace/api-client/types';
import { useRuntimeConfig } from '@borradh-workspace/runtime-config/client';
import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js';
import { CheckIcon } from 'lucide-react';
import { createElement, useEffect, useMemo, useRef, useState } from 'react';

import { useCreateClinicAccount, useGetClinicPage } from '../api';
import { SetupSlides } from './setup-slides';

const STEPS: {
  key: Exclude<ClinicStep, 'preparing'>;
  label: string;
}[] = [
  { key: 'contract', label: 'Contract' },
  { key: 'account', label: 'Your account' },
  { key: 'payment', label: 'Payment' },
  { key: 'setup', label: 'Get set up' },
];

const position = (step: ClinicStep) =>
  step === 'preparing' ? 0 : STEPS.findIndex((s) => s.key === step);

/** Contract · Your account · Payment · Get set up, with the finished ones ticked. */
function Steps({ step }: { step: ClinicStep }) {
  const at = position(step);
  return (
    <ol className="flex items-center gap-2 text-sm" aria-label="Your setup">
      {STEPS.map((s, i) => {
        const done = i < at;
        const current = i === at;
        return (
          <li key={s.key} className="flex items-center gap-2">
            {i > 0 && (
              <span aria-hidden className="h-px w-6 bg-border sm:w-10" />
            )}
            <span
              className={cn(
                'flex size-6 items-center justify-center rounded-full border text-xs font-medium',
                done && 'border-primary bg-primary text-primary-foreground',
                current && 'border-primary text-primary',
                !done && !current && 'text-muted-foreground'
              )}
            >
              {done ? <CheckIcon className="size-3.5" /> : i + 1}
            </span>
            <span
              className={cn(
                current
                  ? 'font-medium text-foreground'
                  : 'text-muted-foreground',
                'hidden sm:inline'
              )}
              aria-current={current ? 'step' : undefined}
            >
              {s.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

const DOCUSEAL_SCRIPT = 'https://cdn.docuseal.com/js/form.js';

/** DocuSeal's own signing form, embedded; `onSigned` fires when they finish. */
function ContractStep({
  src,
  signerName,
  onSigned,
}: { src: string; signerName: string | null; onSigned: () => void }) {
  const ref = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!document.querySelector(`script[src="${DOCUSEAL_SCRIPT}"]`)) {
      const script = document.createElement('script');
      script.src = DOCUSEAL_SCRIPT;
      script.async = true;
      document.head.appendChild(script);
    }
    const el = ref.current;
    el?.addEventListener('completed', onSigned);
    return () => el?.removeEventListener('completed', onSigned);
  }, [onSigned]);
  // A custom element: DocuSeal's script upgrades it into the signing form.
  return createElement('docuseal-form', {
    ref,
    'data-src': src,
    'data-with-title': 'false',
    'data-allow-to-resubmit': 'false',
    // Their name as a typed signature: they confirm it, or switch to drawing.
    ...(signerName ? { 'data-signature': signerName } : {}),
  });
}

/** Stripe's checkout, embedded, for the price the server set. */
function PaymentStep({ clientSecret }: { clientSecret: string }) {
  const { stripePublishableKey } = useRuntimeConfig();
  const stripe = useMemo(
    () =>
      stripePublishableKey ? loadStripeWithCapture(stripePublishableKey) : null,
    [stripePublishableKey]
  );
  if (!stripe)
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Payment can’t load right now. Please try again in a few minutes.
        </AlertDescription>
      </Alert>
    );
  return (
    <EmbeddedCheckoutProvider stripe={stripe} options={{ clientSecret }}>
      <EmbeddedCheckout />
    </EmbeddedCheckoutProvider>
  );
}

/**
 * Their email and business name: makes their Borrah account and signs them
 * in on this page, so Meta and Stripe connect to their own clinic later.
 */
function AccountStep({
  token,
  email,
  onCreated,
}: {
  token: string;
  email: string | null;
  onCreated: () => void;
}) {
  const [values, setValues] = useState({
    email: email ?? '',
    businessName: '',
  });
  const { createAccount, isCreating, error } = useCreateClinicAccount(token, {
    onSuccess: onCreated,
  });
  const signedIn = useSession().data?.user ?? null;
  // Already signed in (they had an account, or a first try stopped after
  // making their login): use that account rather than make another.
  if (signedIn)
    return (
      <form
        className="max-w-md space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          createAccount({
            useCurrentAccount: true,
            ...(signedIn.organizationId
              ? {}
              : { businessName: values.businessName }),
          });
        }}
      >
        <p className="text-muted-foreground">
          You’re signed in to Borrah as{' '}
          <span className="font-medium text-foreground">{signedIn.email}</span>.
          {signedIn.organizationId
            ? ' Continue with this account to finish setting up.'
            : ' Add your business name to finish setting up.'}
        </p>
        {!signedIn.organizationId && (
          <div className="space-y-2">
            <Label htmlFor="setup-business">Business name</Label>
            <Input
              id="setup-business"
              autoComplete="organization"
              required
              value={values.businessName}
              onChange={(e) =>
                setValues({ ...values, businessName: e.target.value })
              }
            />
          </div>
        )}
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error.message}</AlertDescription>
          </Alert>
        )}
        <Button type="submit" size="lg" disabled={isCreating}>
          {isCreating ? 'Setting up…' : `Continue as ${signedIn.email}`}
        </Button>
      </form>
    );
  return (
    <form
      className="max-w-md space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        createAccount(values);
      }}
    >
      <p className="text-muted-foreground">
        This is your login to Borrah. We’ll email you a link to choose a
        password.
      </p>
      <div className="space-y-2">
        <Label htmlFor="setup-email">Email</Label>
        <Input
          id="setup-email"
          type="email"
          autoComplete="email"
          required
          value={values.email}
          onChange={(e) => setValues({ ...values, email: e.target.value })}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="setup-business">Business name</Label>
        <Input
          id="setup-business"
          autoComplete="organization"
          required
          value={values.businessName}
          onChange={(e) =>
            setValues({ ...values, businessName: e.target.value })
          }
        />
      </div>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            {error.message}{' '}
            {/sign in/i.test(error.message) && (
              <a
                className="font-medium underline"
                href={`/sign-in?redirect=${encodeURIComponent(`/start/${token}`)}`}
              >
                Sign in
              </a>
            )}
          </AlertDescription>
        </Alert>
      )}
      <Button type="submit" size="lg" disabled={isCreating}>
        {isCreating ? 'Creating your account…' : 'Create my account'}
      </Button>
    </form>
  );
}

function StepBody({
  token,
  page,
  onAdvance,
}: {
  token: string;
  page: ClinicPage;
  onAdvance: () => void;
}) {
  if (page.step === 'preparing')
    return (
      <p className="text-muted-foreground">
        Your contract is being prepared. Your Borrah contact will let you know
        when it’s ready, and this link will open it.
      </p>
    );
  if (page.step === 'contract' && page.contractSrc)
    return (
      <ContractStep
        src={page.contractSrc}
        signerName={page.signerName}
        onSigned={() => onAdvance()}
      />
    );
  if (page.step === 'account')
    return (
      <AccountStep token={token} email={page.email} onCreated={onAdvance} />
    );
  if (page.step === 'payment' && page.checkoutClientSecret)
    return <PaymentStep clientSecret={page.checkoutClientSecret} />;
  if (page.step === 'setup' && page.calls)
    return (
      <SetupSlides
        token={token}
        calls={page.calls}
        name={page.signerName}
        email={page.email}
      />
    );
  return null;
}

const HEADINGS: Record<ClinicStep, string> = {
  preparing: 'Your contract is on its way',
  contract: 'Read and sign your contract',
  account: 'Create your Borrah account',
  payment: 'Set up your monthly plan',
  setup: 'Get set up',
};

/**
 * The one link a new clinic is sent: sign, create their account, pay, then
 * connect Meta and Stripe and book their calls, always picking up at the
 * first step not yet done.
 */
export function ClinicPageContent({
  token,
  sessionId,
}: {
  token: string;
  sessionId: string | null;
}) {
  const { page, isLoading, isError, error, refetch } = useGetClinicPage(
    token,
    sessionId
  );
  // After signing, DocuSeal's "signed" can take a moment to show; ask a few times.
  const onAdvance = () => {
    let tries = 0;
    const tick = async () => {
      const r = await refetch();
      if (r.data?.step === 'contract' && ++tries < 10) setTimeout(tick, 1500);
    };
    void tick();
  };

  if (isLoading)
    return (
      <Shell>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96 w-full" />
      </Shell>
    );
  if (isError || !page)
    return (
      <Shell>
        <Alert variant="destructive">
          <AlertDescription>
            {error instanceof Error && error.message
              ? error.message
              : 'This link isn’t working. Ask your Borrah contact for a new one.'}
          </AlertDescription>
        </Alert>
      </Shell>
    );

  const shown = page;
  return (
    <Shell>
      <header className="space-y-4">
        <Steps step={shown.step} />
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight text-balance">
            {shown.step === 'contract' && shown.firstName
              ? `Welcome to Borrah, ${shown.firstName}`
              : HEADINGS[shown.step]}
          </h1>
          <p className="text-sm text-muted-foreground">
            {[shown.clinicName, shown.price, `${shown.termMonths} months`]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      </header>
      <StepBody token={token} page={shown} onAdvance={onAdvance} />
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-dvh bg-background">
      <div className="mx-auto flex max-w-3xl flex-col gap-8 px-4 py-10 sm:py-14">
        <span className="text-sm font-semibold tracking-tight">Borrah</span>
        {children}
      </div>
    </main>
  );
}
